import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { useNavigate } from 'react-router-dom';
import { Users, Plus, Trash2, Edit, Eye, BarChart3, Monitor, Search, ChevronDown, ChevronUp, Printer, X, Shield, ToggleLeft, ToggleRight, FolderOpen, Check, Upload, FileSpreadsheet, Target, HelpCircle, Save, MessageSquare, Bug, Lightbulb, Cpu, Power, MapPin, GraduationCap } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import UsageTab from './UsageTab';
import AppControlTab from './AppControlTab';
import EvaluationGridTab from './EvaluationGridTab';
import AddressesTab from './AddressesTab';
import CorrectionsTab from './CorrectionsTab';
import { buildPreArrivalPrintHtml, formatChrono, SCENE_CHECKS } from '@/components/health/PreArrivalPhase';
import { buildPhasesHtml, buildNotesHtml } from '@/components/health/callReport';

const RAOAdmin = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [students, setStudents] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [p1Stats, setP1Stats] = useState([]);
  const [loading, setLoading] = useState(false);

  // Student form
  const [showForm, setShowForm] = useState(false);
  const [editingStudent, setEditingStudent] = useState(null);
  const [form, setForm] = useState({ first_name: '', last_name: '', student_number: '', email: '', password: '' });

  // Student sessions view
  const [viewingSessions, setViewingSessions] = useState(null);
  const [studentSessions, setStudentSessions] = useState([]);
  const [selectedSessionIds, setSelectedSessionIds] = useState(new Set());

  const [searchQuery, setSearchQuery] = useState('');
  const [filterGroup, setFilterGroup] = useState('all');

  // Groups
  const [groups, setGroups] = useState([]);
  const [showGroupForm, setShowGroupForm] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [groupForm, setGroupForm] = useState({ name: '', permissions: { scenarios: true, appels_911: true, appels_assignes: true, historique: true, rao: true, ambulance: false, admin: false }, rao_levels: ['debutant', 'intermediaire', 'avance'], enabled_health_protocols: null });
  const [assigningGroup, setAssigningGroup] = useState(null);
  const [healthProtocolsCatalog, setHealthProtocolsCatalog] = useState([]);

  // Import
  const fileInputRef = useRef(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  // Help articles
  const [helpArticles, setHelpArticles] = useState([]);
  const [editingArticle, setEditingArticle] = useState(null);
  const [articleForm, setArticleForm] = useState({ title: '', content: '', category: 'etudiant', order: 0 });

  // Comments
  const [comments, setComments] = useState([]);
  const [commentFilter, setCommentFilter] = useState('all');
  const [showArticleForm, setShowArticleForm] = useState(false);

  // Data loading
  const loadStudents = useCallback(async () => {
    try {
      const resp = await axios.get(`${API}/rao/admin/students`);
      setStudents(resp.data);
    } catch (err) {
      console.error(err);
    }
  }, []);

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await axios.get(`${API}/rao/admin/dashboard`);
      setDashboard(resp.data);
      const p1Resp = await axios.get(`${API}/rao/admin/health/p1-stats`);
      setP1Stats(p1Resp.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadGroups = useCallback(async () => {
    try {
      const resp = await axios.get(`${API}/rao/admin/groups`);
      setGroups(resp.data);
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => {
    loadStudents();
    loadDashboard();
    loadGroups();
    loadCommentCount();
    loadPendingEvalCount();
    // Load full Health protocol catalog (admin view = no student filter, returns all)
    axios.get(`${API}/health/protocols`)
      .then(resp => setHealthProtocolsCatalog(resp.data || []))
      .catch(() => {});
  }, [loadStudents, loadDashboard, loadGroups]);

  const [newCommentCount, setNewCommentCount] = useState(0);
  const loadCommentCount = async () => {
    try {
      const resp = await axios.get(`${API}/comments`);
      setNewCommentCount(resp.data.filter(c => c.status === 'nouveau').length);
    } catch (err) { console.warn('loadCommentCount failed:', err); }
  };

  const [pendingEvalCount, setPendingEvalCount] = useState(0);
  const loadPendingEvalCount = async () => {
    try {
      const resp = await axios.get(`${API}/admin/evaluation-requests`, { params: { status: 'pending' } });
      setPendingEvalCount((resp.data || []).length);
    } catch (err) { console.warn('loadPendingEvalCount failed:', err); }
  };

  // Student CRUD
  const resetForm = () => {
    setForm({ first_name: '', last_name: '', student_number: '', email: '', password: '' });
    setEditingStudent(null);
    setShowForm(false);
  };

  const saveStudent = async (e) => {
    e.preventDefault();
    try {
      if (editingStudent) {
        await axios.put(`${API}/rao/admin/students/${editingStudent.id}`, form);
        toast.success('Étudiant modifié');
      } else {
        await axios.post(`${API}/rao/admin/students`, form);
        toast.success('Étudiant créé');
      }
      resetForm();
      loadStudents();
      loadDashboard();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur');
    }
  };

  const deleteStudent = async (id, name) => {
    if (!window.confirm(`Supprimer l'étudiant ${name} ?`)) return;
    try {
      await axios.delete(`${API}/rao/admin/students/${id}`);
      toast.success('Étudiant supprimé');
      loadStudents();
      loadDashboard();
    } catch (err) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const editStudent = (student) => {
    setForm({
      first_name: student.first_name,
      last_name: student.last_name,
      student_number: student.student_number,
      email: student.email,
      password: ''
    });
    setEditingStudent(student);
    setShowForm(true);
  };

  const viewStudentSessions = async (student) => {
    try {
      const resp = await axios.get(`${API}/rao/admin/students/${student.id}/sessions`);
      setStudentSessions(resp.data.sessions);
      setViewingSessions(student);
      setSelectedSessionIds(new Set());
    } catch (err) {
      toast.error('Erreur lors du chargement');
    }
  };

  const toggleSessionSelect = (id) => {
    setSelectedSessionIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAllSessions = () => {
    if (selectedSessionIds.size === studentSessions.length) {
      setSelectedSessionIds(new Set());
    } else {
      setSelectedSessionIds(new Set(studentSessions.map(s => s.id)));
    }
  };

  const deleteSelectedSessions = async () => {
    if (selectedSessionIds.size === 0) return;
    const count = selectedSessionIds.size;
    if (!window.confirm(`Supprimer ${count} session${count > 1 ? 's' : ''} ?`)) return;
    try {
      await Promise.all([...selectedSessionIds].map(id => axios.delete(`${API}/rao/admin/sessions/${id}`)));
      setStudentSessions(prev => prev.filter(s => !selectedSessionIds.has(s.id)));
      setSelectedSessionIds(new Set());
      toast.success(`${count} session${count > 1 ? 's' : ''} supprimée${count > 1 ? 's' : ''}`);
      loadDashboard();
    } catch (err) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const deleteOneSession = async (sessionId) => {
    try {
      await axios.delete(`${API}/rao/admin/sessions/${sessionId}`);
      setStudentSessions(prev => prev.filter(s => s.id !== sessionId));
      toast.success('Session supprimée');
      loadDashboard();
    } catch (err) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Print a health session — admin variant. Fetches the protocol to access labels
  // (phase titles, priority resources, etc.) then opens a print window.
  const printHealthSession = async (session) => {
    let protocol = null;
    try {
      const resp = await axios.get(`${API}/health/protocols/${session.protocol_id}`);
      protocol = resp.data;
    } catch {
      toast.error('Impossible de charger le protocole pour impression');
      return;
    }
    const PRIORITY_CFG = {
      1: { label: 'PRIORITÉ 1', title: 'Critique', color: '#DC2626', bg: '#FEE2E2' },
      2: { label: 'PRIORITÉ 2', title: 'Urgent', color: '#F59E0B', bg: '#FEF3C7' },
      4: { label: 'PRIORITÉ 4', title: 'Stable', color: '#22C55E', bg: '#DCFCE7' },
    };
    const p = PRIORITY_CFG[session.final_priority] || PRIORITY_CFG[4];
    const pData = protocol?.priorities?.[String(session.final_priority)];
    const answers = session.answers || {};
    const redFlags = session.red_flags_detected || [];
    const duration = session.duration_seconds || 0;
    const fmt = (s) => Math.floor(s / 60) + ':' + String(s % 60).padStart(2, '0');
    const esc = (s) => String(s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

    const printWindow = window.open('', '_blank');
    if (!printWindow) { toast.error('Autorisez les popups'); return; }

    // Phase 1 RAO info
    const phase1 = (protocol.phases || []).find(ph => ph.type === 'info_gathering');
    const phase1Entries = (phase1?.fields || [])
      .map(f => ({ label: f.label, value: (answers[f.id] || '').toString().trim() }))
      .filter(e => e.value);
    const phase1Html = phase1Entries.length > 0
      ? '<div style="margin-top:16px;padding:12px;border-left:4px solid #2E6CF5;background:#EFF6FF;border-radius:8px;">'
        + '<h4 style="color:#1e40af;font-size:14px;font-weight:bold;margin-bottom:8px;">Informations RAO notées par l\'étudiant</h4>'
        + '<table style="width:100%;border-collapse:collapse;font-size:13px;">'
        + phase1Entries.map(e => '<tr><td style="padding:3px 8px 3px 0;color:#1e3a8a;font-weight:600;width:40%;vertical-align:top;">' + esc(e.label) + '</td><td style="padding:3px 0;color:#1e293b;">' + esc(e.value) + '</td></tr>').join('')
        + '</table></div>'
      : '';

    // Render each phase's answers (excluding phase 1 already rendered) — shared builder with chrono badges
    const phaseHtml = buildPhasesHtml(protocol, answers, session.phase_times);
    const notesHtml = buildNotesHtml(session.call_notes);

    const rfHtml = redFlags.length > 0
      ? '<div style="margin-top:16px;padding:12px;border-left:4px solid #DC2626;background:#FEF2F2;border-radius:8px;">'
        + '<h4 style="color:#DC2626;font-size:14px;font-weight:bold;margin-bottom:8px;">Drapeaux rouges détectés (' + redFlags.length + ')</h4>'
        + '<ul style="list-style:none;padding:0;">' + redFlags.map(f => '<li style="font-size:13px;color:#B91C1C;padding:2px 0;">⚠ ' + esc(f) + '</li>').join('') + '</ul></div>'
      : '';

    const resourcesHtml = pData
      ? '<div style="margin-top:16px;"><h4 style="font-size:14px;font-weight:bold;">Ressources à déployer</h4><ul>' + (pData.resources || []).map(r => '<li style="font-size:13px;">' + esc(r) + '</li>').join('') + '</ul>'
        + '<h4 style="font-size:14px;font-weight:bold;margin-top:12px;">Consignes pré-arrivée</h4><ol>' + (pData.pre_arrival || []).map(c => '<li style="font-size:13px;">' + esc(c) + '</li>').join('') + '</ol></div>'
      : '';

    // Volet 3 — P1 pre-arrival performance section (if the session reached P1)
    const preArrivalHtml = (session.p1_to_end_seconds != null && session.pre_arrival)
      ? buildPreArrivalPrintHtml(session.pre_arrival, session.p1_to_end_seconds, ((protocol?.priorities?.['1'] || {}).pre_arrival || []).length)
      : '';

    const studentLine = (viewingSessions ? (viewingSessions.first_name + ' ' + viewingSessions.last_name + ' (#' + (viewingSessions.student_number || '—') + ')') : (session.student_name || '—'));

    printWindow.document.write('<!DOCTYPE html><html><head><title>Santé — ' + esc(protocol.title) + ' — ' + esc(studentLine) + '</title>'
      + '<style>*{margin:0;padding:0;box-sizing:border-box;}body{font-family:Segoe UI,Arial,sans-serif;padding:40px;color:#1e293b;line-height:1.6;}'
      + '.header{text-align:center;margin-bottom:24px;padding-bottom:16px;border-bottom:2px solid #e2e8f0;}'
      + '.meta{display:flex;gap:30px;flex-wrap:wrap;padding:12px 16px;background:#f8fafc;border-radius:8px;margin-bottom:16px;}'
      + '.meta-item{font-size:13px;}</style></head><body>'
      + '<div class="header"><h1 style="font-size:20px;color:#002768;">Protocole Santé — ' + esc(protocol.title) + '</h1>'
      + '<p style="font-size:12px;color:#64748b;">Rapport d\'instructeur — Simulateur 911</p></div>'
      + '<div class="meta"><div class="meta-item"><strong>Étudiant:</strong> ' + esc(studentLine) + '</div>'
      + '<div class="meta-item"><strong>Date:</strong> ' + new Date(session.started_at || session.completed_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' }) + '</div>'
      + '<div class="meta-item"><strong>Mode:</strong> ' + (session.mode === 'guided' ? 'Guidé' : 'Simulation') + '</div>'
      + '<div class="meta-item"><strong>Durée:</strong> ' + fmt(duration) + '</div>'
      + '<div class="meta-item"><strong>Statut:</strong> ' + (session.status === 'completed' ? 'Complétée' : 'En cours') + '</div></div>'
      + '<div style="padding:16px;border:2px solid ' + p.color + ';background:' + p.bg + ';border-radius:12px;margin-bottom:16px;">'
      + '<h2 style="font-size:18px;color:' + p.color + ';">' + p.label + ' — ' + p.title + '</h2></div>'
      + phase1Html + notesHtml + rfHtml + preArrivalHtml
      + (phaseHtml || '<h3 style="font-size:15px;font-weight:bold;color:#002768;margin:18px 0 10px 0;">Déroulement complet de l\'appel</h3><div style="font-size:13px;color:#94a3b8;font-style:italic;">Aucune phase complétée.</div>')
      + resourcesHtml
      + '<div style="margin-top:24px;text-align:center;font-size:11px;color:#94a3b8;">Simulateur 911 — Imprimé le ' + new Date().toLocaleDateString('fr-FR') + '</div>'
      + '</body></html>');
    printWindow.document.close();
    setTimeout(() => printWindow.print(), 300);
  };

  const PERM_LABELS = [
    { key: 'tableau_de_bord', label: 'Accueil', color: '#6b7280' },
    { key: 'scenarios', label: 'Scénarios', color: '#002768' },
    { key: 'appels_911', label: 'Appels 911', color: '#2E6CF5' },
    { key: 'appels_assignes', label: 'Assignés', color: '#2E6CF5' },
    { key: 'rao', label: 'RAO', color: '#24B219' },
    { key: 'ambulance', label: 'Santé', color: '#EE2130' },
    { key: 'historique', label: 'Historique', color: '#002768' },
    { key: 'admin', label: 'Admin', color: '#EE2130' },
  ];

  const RAO_LEVEL_LABELS = [
    { key: 'debutant', label: 'Débutant', color: '#24B219' },
    { key: 'intermediaire', label: 'Intermédiaire', color: '#D97706' },
    { key: 'avance', label: 'Avancé', color: '#EE2130' },
  ];

  const togglePermission = async (student, permKey) => {
    const currentPerms = student.permissions || { scenarios: true, appels_911: true, appels_assignes: true, historique: true, rao: true, ambulance: false, admin: false };
    const updated = { ...currentPerms, [permKey]: !currentPerms[permKey] };
    // Optimistic update first so UI reflects state instantly even if the request hangs
    setStudents(prev => prev.map(s => s.id === student.id ? { ...s, permissions: updated } : s));
    try {
      await axios.put(`${API}/rao/admin/students/${student.id}/permissions`, { permissions: updated });
      toast.success(`${PERM_LABELS.find(p => p.key === permKey)?.label} — ${updated[permKey] ? 'Activé' : 'Désactivé'}`);
    } catch (err) {
      console.warn('togglePermission failed:', err);
      // Roll back optimistic update on failure
      setStudents(prev => prev.map(s => s.id === student.id ? { ...s, permissions: currentPerms } : s));
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const toggleStudentRaoLevel = async (student, level) => {
    const currentLevels = student.rao_levels || ['debutant', 'intermediaire', 'avance'];
    const updated = currentLevels.includes(level)
      ? currentLevels.filter(l => l !== level)
      : [...currentLevels, level];
    try {
      await axios.put(`${API}/rao/admin/students/${student.id}/permissions`, { 
        permissions: student.permissions,
        rao_levels: updated 
      });
      setStudents(prev => prev.map(s => s.id === student.id ? { ...s, rao_levels: updated } : s));
      const label = RAO_LEVEL_LABELS.find(l => l.key === level)?.label;
      toast.success(`RAO ${label} — ${updated.includes(level) ? 'Activé' : 'Désactivé'}`);
    } catch (err) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  // Group CRUD
  const resetGroupForm = () => {
    setGroupForm({ name: '', permissions: { scenarios: true, appels_911: true, appels_assignes: true, historique: true, rao: true, ambulance: false, admin: false }, rao_levels: ['debutant', 'intermediaire', 'avance'], enabled_health_protocols: null });
    setEditingGroup(null);
    setShowGroupForm(false);
  };

  const saveGroup = async (e) => {
    e.preventDefault();
    try {
      if (editingGroup) {
        await axios.put(`${API}/rao/admin/groups/${editingGroup.id}`, groupForm);
        toast.success('Groupe modifié');
      } else {
        await axios.post(`${API}/rao/admin/groups`, groupForm);
        toast.success('Groupe créé');
      }
      resetGroupForm();
      loadGroups();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur');
    }
  };

  const deleteGroup = async (id, name) => {
    if (!window.confirm(`Supprimer le groupe "${name}" ? Les étudiants ne seront pas supprimés.`)) return;
    try {
      await axios.delete(`${API}/rao/admin/groups/${id}`);
      toast.success('Groupe supprimé');
      loadGroups();
      loadStudents();
    } catch (err) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const applyGroupPermissions = async (group) => {
    if (!window.confirm(`Appliquer les permissions du groupe "${group.name}" à tous ses membres ?`)) return;
    try {
      const resp = await axios.put(`${API}/rao/admin/groups/${group.id}/apply-permissions`);
      toast.success(`Permissions appliquées à ${resp.data.updated_count} étudiant(s)`);
      loadStudents();
    } catch (err) {
      toast.error('Erreur');
    }
  };

  const toggleStudentGroup = async (student, groupId) => {
    const currentGroups = student.groups || [];
    const updated = currentGroups.includes(groupId)
      ? currentGroups.filter(g => g !== groupId)
      : [...currentGroups, groupId];
    try {
      await axios.put(`${API}/rao/admin/students/${student.id}/groups`, { groups: updated });
      setStudents(prev => prev.map(s => s.id === student.id ? { ...s, groups: updated } : s));
    } catch (err) {
      toast.error('Erreur');
    }
  };

  // Help articles functions
  const loadHelpArticles = useCallback(async () => {
    try {
      const resp = await axios.get(`${API}/help/articles`);
      setHelpArticles(resp.data);
    } catch (err) {
      console.error('Error loading help articles:', err);
    }
  }, []);

  useEffect(() => { if (activeTab === 'help') loadHelpArticles(); }, [activeTab, loadHelpArticles]);

  const saveArticle = async () => {
    if (!articleForm.title.trim() || !articleForm.content.trim()) {
      toast.error('Titre et contenu requis');
      return;
    }
    try {
      if (editingArticle) {
        await axios.put(`${API}/help/articles/${editingArticle.id}`, articleForm);
        toast.success('Article modifié');
      } else {
        await axios.post(`${API}/help/articles`, { ...articleForm, order: helpArticles.length + 1 });
        toast.success('Article créé');
      }
      setEditingArticle(null);
      setArticleForm({ title: '', content: '', category: 'etudiant', order: 0 });
      setShowArticleForm(false);
      loadHelpArticles();
    } catch (err) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const deleteArticle = async (id) => {
    if (!window.confirm('Supprimer cet article ?')) return;
    try {
      await axios.delete(`${API}/help/articles/${id}`);
      toast.success('Article supprimé');
      loadHelpArticles();
    } catch (err) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const startEditArticle = (article) => {
    setEditingArticle(article);
    setArticleForm({ title: article.title, content: article.content, category: article.category, order: article.order });
    setShowArticleForm(true);
  };

  // Comments functions
  const loadComments = useCallback(async () => {
    try {
      const resp = await axios.get(`${API}/comments`);
      setComments(resp.data);
    } catch (err) {
      console.error('Error loading comments:', err);
    }
  }, []);

  useEffect(() => { if (activeTab === 'comments') loadComments(); }, [activeTab, loadComments]);

  const updateCommentStatus = async (id, status) => {
    try {
      await axios.put(`${API}/comments/${id}`, { status });
      loadComments();
    } catch { toast.error('Erreur'); }
  };

  const deleteComment = async (id) => {
    try {
      await axios.delete(`${API}/comments/${id}`);
      toast.success('Commentaire supprimé');
      loadComments();
    } catch { toast.error('Erreur'); }
  };


  const toggleGroupPerm = (key) => {
    setGroupForm(prev => ({ ...prev, permissions: { ...prev.permissions, [key]: !prev.permissions[key] } }));
  };

  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error('Veuillez sélectionner un fichier Excel (.xlsx)');
      return;
    }

    setImporting(true);
    setImportResult(null);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const resp = await axios.post(`${API}/rao/admin/students/import`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setImportResult(resp.data);
      if (resp.data.created > 0) {
        toast.success(`${resp.data.created} étudiant(s) importé(s) avec succès!`);
        loadStudents();
        loadDashboard();
      } else {
        toast.info('Aucun nouvel étudiant importé (tous déjà existants)');
      }
    } catch (err) {
      toast.error(err.response?.data?.error || "Erreur lors de l'importation");
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const filteredStudents = students.filter(s => {
    // Group filter
    if (filterGroup !== 'all') {
      if (filterGroup === 'none') {
        if ((s.groups || []).length > 0) return false;
      } else {
        if (!(s.groups || []).includes(filterGroup)) return false;
      }
    }
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return `${s.first_name} ${s.last_name}`.toLowerCase().includes(q)
      || s.email.toLowerCase().includes(q)
      || s.student_number.toLowerCase().includes(q);
  });

  // ---- Login Screen ----
  // ---- Admin Panel ----
  return (
    <div className="min-h-screen pt-20 px-4 sm:px-6 lg:px-8 pb-12" style={{ background: 'linear-gradient(135deg, #f8fbff 0%, #CAE9FA 40%, #f0fdf4 100%)' }}>
      {/* Admin sub-tabs */}
      <div className="max-w-7xl mx-auto mb-6">
        <div className="flex items-center space-x-1 glass rounded-xl p-1 w-fit">
          {[
            { id: 'dashboard', icon: BarChart3, label: 'Tableau de bord' },
            { id: 'students', icon: Users, label: 'Étudiants' },
            { id: 'groups', icon: FolderOpen, label: 'Groupes' },
            { id: 'corrections', icon: GraduationCap, label: 'Corrections' },
            { id: 'evaluation', icon: Target, label: 'Évaluation' },
            { id: 'help', icon: HelpCircle, label: 'Aide' },
            { id: 'comments', icon: MessageSquare, label: 'Commentaires' },
            { id: 'usage', icon: Cpu, label: 'Utilisation' },
            { id: 'addresses', icon: MapPin, label: 'Adresses' },
            { id: 'control', icon: Power, label: 'Contrôle' },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => { setActiveTab(tab.id); setViewingSessions(null); }}
              data-testid={`admin-tab-${tab.id}`}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                activeTab === tab.id ? 'text-white' : 'text-gray-600 hover:bg-white/50'
              }`}
              style={activeTab === tab.id ? { backgroundColor: '#002768' } : {}}
            >
              <tab.icon className="w-4 h-4" />
              <span className="hidden sm:inline">{tab.label}</span>
              {tab.id === 'comments' && newCommentCount > 0 && (
                <span className="w-5 h-5 flex items-center justify-center rounded-full text-xs font-bold text-white" style={{ backgroundColor: '#EE2130' }}>
                  {newCommentCount}
                </span>
              )}
              {tab.id === 'evaluation' && pendingEvalCount > 0 && (
                <span className="w-5 h-5 flex items-center justify-center rounded-full text-xs font-bold text-white" style={{ backgroundColor: '#F59E0B' }} data-testid="eval-tab-badge">
                  {pendingEvalCount}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-7xl mx-auto">

        {/* ---- DASHBOARD TAB ---- */}
        {activeTab === 'dashboard' && (
          <div>
            <h2 className="text-2xl font-bold mb-6" style={{ color: '#002768' }}>Tableau de bord</h2>

            {/* Stats */}
            {dashboard && (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                  <Card className="glass border-0">
                    <CardContent className="pt-6 text-center">
                      <div className="text-4xl font-bold" style={{ color: '#002768' }}>{dashboard.total_students}</div>
                      <p className="text-sm text-gray-500 mt-1">Étudiants inscrits</p>
                    </CardContent>
                  </Card>
                  <Card className="glass border-0">
                    <CardContent className="pt-6 text-center">
                      <div className="text-4xl font-bold" style={{ color: '#2E6CF5' }}>{dashboard.total_sessions}</div>
                      <p className="text-sm text-gray-500 mt-1">Sessions totales</p>
                    </CardContent>
                  </Card>
                  <Card className="glass border-0">
                    <CardContent className="pt-6 text-center">
                      <div className="text-4xl font-bold" style={{ color: '#2E6CF5' }}>{dashboard.sim_sessions || 0}</div>
                      <p className="text-sm text-gray-500 mt-1">Sessions Simulateur</p>
                    </CardContent>
                  </Card>
                  <Card className="glass border-0">
                    <CardContent className="pt-6 text-center">
                      <div className="text-4xl font-bold" style={{ color: '#24B219' }}>{dashboard.rao_sessions || 0}</div>
                      <p className="text-sm text-gray-500 mt-1">Sessions RAO</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Per-student stats */}
                <Card className="glass border-0">
                  <CardHeader>
                    <CardTitle>Activité par étudiant</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {dashboard.student_stats.length === 0 ? (
                      <p className="text-gray-500 text-center py-4">Aucun étudiant inscrit</p>
                    ) : (
                      <div className="space-y-2">
                        {dashboard.student_stats.map((stat) => (
                          <div key={stat.student.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div>
                              <span className="font-medium text-gray-800">
                                {stat.student.first_name} {stat.student.last_name}
                              </span>
                              <span className="ml-2 text-sm text-gray-500">#{stat.student.student_number}</span>
                            </div>
                            <div className="flex items-center gap-3">
                              <Badge variant="outline" className="text-white" style={{ backgroundColor: '#2E6CF5' }}>
                                {stat.total_sessions} session{stat.total_sessions !== 1 ? 's' : ''}
                              </Badge>
                              <Badge variant="outline" className="text-white" style={{ backgroundColor: '#24B219' }}>
                                {stat.completed_sessions} complétée{stat.completed_sessions !== 1 ? 's' : ''}
                              </Badge>
                              <button
                                onClick={() => { viewStudentSessions(stat.student); setActiveTab('students'); }}
                                className="p-1.5 rounded-lg hover:bg-gray-200 text-gray-600"
                                title="Voir les sessions"
                              >
                                <Eye className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Volet 3 — P1 pre-arrival performance comparison */}
                {p1Stats.length > 0 && (
                  <Card className="glass border-0 mt-6" data-testid="p1-performance-card">
                    <CardHeader>
                      <CardTitle>🚑 Performance P1 — Consignes pré-arrivée</CardTitle>
                      <CardDescription>Sessions Santé où une Priorité 1 a été déclenchée (délai avant raccrochage, consignes données au témoin)</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="text-left text-xs uppercase tracking-wide text-gray-500 border-b">
                              <th className="py-2 pr-3">Étudiant</th>
                              <th className="py-2 pr-3 text-center">Sessions P1</th>
                              <th className="py-2 pr-3 text-center">Délai moyen P1 → fin</th>
                              <th className="py-2 pr-3 text-center">Consignes données</th>
                              <th className="py-2 pr-3 text-center">Sécurisation (/{SCENE_CHECKS.length})</th>
                              <th className="py-2 text-right">Dernière session</th>
                            </tr>
                          </thead>
                          <tbody>
                            {p1Stats.map(row => (
                              <tr key={row.student_number || row.student_name} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`p1-stat-row-${row.student_number}`}>
                                <td className="py-2 pr-3">
                                  <span className="font-medium text-gray-800">{row.student_name || '—'}</span>
                                  {row.student_number && <span className="ml-2 text-xs text-gray-400">#{row.student_number}</span>}
                                </td>
                                <td className="py-2 pr-3 text-center font-semibold" style={{ color: '#DC2626' }}>{row.p1_sessions}</td>
                                <td className="py-2 pr-3 text-center font-mono">{formatChrono(row.avg_delay_seconds)}</td>
                                <td className="py-2 pr-3 text-center">
                                  <span className="font-semibold">{row.avg_instructions_given}</span>
                                  {row.instructions_pct != null && (
                                    <span className={`ml-1.5 px-1.5 py-0.5 rounded-full text-[11px] font-bold ${row.instructions_pct >= 70 ? 'bg-green-100 text-green-700' : row.instructions_pct >= 40 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>
                                      {row.instructions_pct}%
                                    </span>
                                  )}
                                </td>
                                <td className="py-2 pr-3 text-center">{row.avg_scene_checks}</td>
                                <td className="py-2 text-right text-xs text-gray-500">
                                  {row.last_session_at ? new Date(row.last_session_at).toLocaleDateString('fr-CA', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </>
            )}
          </div>
        )}

        {/* ---- STUDENTS TAB ---- */}
        {activeTab === 'students' && !viewingSessions && (
          <div>
            <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
              <h2 className="text-2xl font-bold" style={{ color: '#002768' }}>Gestion des étudiants</h2>
              <div className="flex items-center gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleImportFile}
                  className="hidden"
                  data-testid="import-file-input"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={importing}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium border-2 transition-all hover:shadow-md disabled:opacity-50"
                  style={{ borderColor: '#24B219', color: '#24B219' }}
                  data-testid="import-excel-button"
                >
                  {importing ? (
                    <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <FileSpreadsheet className="w-4 h-4" />
                  )}
                  {importing ? 'Importation...' : 'Importer Excel'}
                </button>
                <button
                  onClick={() => { resetForm(); setShowForm(true); }}
                  className="flex items-center gap-2 px-4 py-2 text-white rounded-lg font-medium hover:opacity-90"
                  style={{ backgroundColor: '#002768' }}
                  data-testid="add-student-button"
                >
                  <Plus className="w-4 h-4" />
                  Ajouter un étudiant
                </button>
              </div>
            </div>

            {/* Import Result */}
            {importResult && (
              <Card className="border-2 mb-6" style={{ borderColor: '#24B219', backgroundColor: '#f0fdf4' }}>
                <CardContent className="pt-5 pb-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-bold text-lg" style={{ color: '#002768' }}>
                        Résultat de l'importation
                      </h3>
                      <div className="flex items-center gap-4 mt-2 text-sm">
                        <span className="flex items-center gap-1 font-semibold" style={{ color: '#24B219' }}>
                          <Check className="w-4 h-4" /> {importResult.created} créé(s)
                        </span>
                        {importResult.skipped_count > 0 && (
                          <span className="text-amber-600 font-medium">
                            {importResult.skipped_count} ignoré(s) (doublon)
                          </span>
                        )}
                        {importResult.errors?.length > 0 && (
                          <span className="text-rose-600 font-medium">
                            {importResult.errors.length} erreur(s)
                          </span>
                        )}
                      </div>
                      {importResult.skipped?.length > 0 && (
                        <div className="mt-2 text-xs text-gray-600">
                          <span className="font-medium">Ignorés :</span>{' '}
                          {importResult.skipped.join(', ')}
                        </div>
                      )}
                      {importResult.errors?.length > 0 && (
                        <div className="mt-1 text-xs text-rose-600">
                          {importResult.errors.map((err, i) => <div key={i}>{err}</div>)}
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => setImportResult(null)}
                      className="p-1 text-gray-400 hover:text-gray-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Search + Group Filter */}
            <div className="flex gap-3 mb-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Rechercher par nom, courriel ou numéro..."
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2"
                  style={{ '--tw-ring-color': '#2E6CF5' }}
                  data-testid="search-students"
                />
              </div>
              {groups.length > 0 && (
                <select
                  value={filterGroup}
                  onChange={(e) => setFilterGroup(e.target.value)}
                  className="px-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 text-sm"
                  style={{ '--tw-ring-color': '#2E6CF5' }}
                  data-testid="filter-group"
                >
                  <option value="all">Tous les groupes</option>
                  <option value="none">Sans groupe</option>
                  {groups.map(g => (
                    <option key={g.id} value={g.id}>{g.name} ({g.member_count})</option>
                  ))}
                </select>
              )}
            </div>

            {/* Form */}
            {showForm && (
              <Card className="glass border-0 mb-6">
                <CardHeader>
                  <CardTitle>{editingStudent ? 'Modifier l\'étudiant' : 'Nouvel étudiant'}</CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={saveStudent} className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Prénom</label>
                        <input
                          type="text"
                          value={form.first_name}
                          onChange={(e) => setForm({...form, first_name: e.target.value})}
                          className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:outline-none"
                          style={{ '--tw-ring-color': '#2E6CF5' }}
                          required
                          data-testid="student-first-name"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Nom</label>
                        <input
                          type="text"
                          value={form.last_name}
                          onChange={(e) => setForm({...form, last_name: e.target.value})}
                          className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:outline-none"
                          style={{ '--tw-ring-color': '#2E6CF5' }}
                          required
                          data-testid="student-last-name"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Numéro étudiant</label>
                        <input
                          type="text"
                          value={form.student_number}
                          onChange={(e) => setForm({...form, student_number: e.target.value})}
                          className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:outline-none"
                          style={{ '--tw-ring-color': '#2E6CF5' }}
                          required
                          data-testid="student-number"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Courriel <span className="text-gray-400 font-normal">(optionnel)</span></label>
                        <input
                          type="email"
                          value={form.email}
                          onChange={(e) => setForm({...form, email: e.target.value})}
                          className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:outline-none"
                          style={{ '--tw-ring-color': '#2E6CF5' }}
                          placeholder="Peut être ajouté plus tard"
                          data-testid="student-email"
                        />
                      </div>
                      <div className="sm:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {editingStudent ? 'Nouveau mot de passe (laisser vide pour garder l\'actuel)' : 'Mot de passe'}
                        </label>
                        <input
                          type="text"
                          value={form.password}
                          onChange={(e) => setForm({...form, password: e.target.value})}
                          className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:outline-none"
                          style={{ '--tw-ring-color': '#2E6CF5' }}
                          required={false}
                          placeholder={editingStudent ? "Laisser vide pour garder l'actuel" : 'Par défaut: numéro étudiant'}
                          data-testid="student-password"
                        />
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <button
                        type="submit"
                        className="px-6 py-2 text-white rounded-lg font-medium hover:opacity-90"
                        style={{ backgroundColor: '#002768' }}
                        data-testid="save-student-button"
                      >
                        {editingStudent ? 'Enregistrer' : 'Créer l\'étudiant'}
                      </button>
                      <button
                        type="button"
                        onClick={resetForm}
                        className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50"
                      >
                        Annuler
                      </button>
                    </div>
                  </form>
                </CardContent>
              </Card>
            )}

            {/* Student list */}
            <Card className="glass border-0">
              <CardContent className="pt-6">
                {filteredStudents.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">
                    {students.length === 0 ? 'Aucun étudiant inscrit' : 'Aucun résultat trouvé'}
                  </p>
                ) : (
                  <div className="space-y-2">
                    {filteredStudents.map(student => {
                      const perms = student.permissions || { scenarios: true, appels_911: true, appels_assignes: true, historique: true, rao: true };
                      return (
                        <div key={student.id} className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <div className="font-medium text-gray-800">
                                {student.first_name} {student.last_name}
                              </div>
                              <div className="flex items-center gap-3 text-sm text-gray-500">
                                <span>#{student.student_number}</span>
                                <span>{student.email}</span>
                              </div>
                            </div>
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => viewStudentSessions(student)}
                                className="p-2 rounded-lg hover:bg-blue-100"
                                style={{ color: '#2E6CF5' }}
                                title="Voir les sessions"
                              >
                                <Eye className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => editStudent(student)}
                                className="p-2 rounded-lg hover:bg-amber-100 text-amber-600"
                                title="Modifier"
                              >
                                <Edit className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => deleteStudent(student.id, `${student.first_name} ${student.last_name}`)}
                                className="p-2 rounded-lg hover:bg-rose-100 text-rose-600"
                                title="Supprimer"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                          {/* Permission toggles */}
                          <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-gray-200">
                            <span className="text-xs text-gray-500 font-medium self-center mr-1">
                              <Shield className="w-3.5 h-3.5 inline -mt-0.5 mr-0.5" />
                              Accès :
                            </span>
                            {PERM_LABELS.map(perm => (
                              <button
                                key={perm.key}
                                onClick={() => togglePermission(student, perm.key)}
                                className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-all ${
                                  perms[perm.key]
                                    ? 'text-white'
                                    : 'bg-gray-200 text-gray-500'
                                }`}
                                style={perms[perm.key] ? { backgroundColor: perm.color } : {}}
                                data-testid={`perm-toggle-${student.id}-${perm.key}`}
                                title={`${perms[perm.key] ? 'Désactiver' : 'Activer'} ${perm.label}`}
                              >
                                {perms[perm.key]
                                  ? <ToggleRight className="w-3.5 h-3.5" />
                                  : <ToggleLeft className="w-3.5 h-3.5" />
                                }
                                {perm.label}
                              </button>
                            ))}
                          </div>
                          {/* RAO Level toggles */}
                          <div className="flex flex-wrap gap-2 mt-2 pt-2 border-t border-dashed border-gray-200">
                            <span className="text-xs text-gray-500 font-medium self-center mr-1">
                              <Target className="w-3.5 h-3.5 inline -mt-0.5 mr-0.5" />
                              Niveaux RAO :
                            </span>
                            {RAO_LEVEL_LABELS.map(lvl => {
                              const studentLevels = student.rao_levels || ['debutant', 'intermediaire', 'avance'];
                              const isActive = studentLevels.includes(lvl.key);
                              return (
                                <button
                                  key={lvl.key}
                                  onClick={() => toggleStudentRaoLevel(student, lvl.key)}
                                  className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-all ${
                                    isActive ? 'text-white' : 'bg-gray-200 text-gray-500'
                                  }`}
                                  style={isActive ? { backgroundColor: lvl.color } : {}}
                                  data-testid={`rao-level-${student.id}-${lvl.key}`}
                                  title={`${isActive ? 'Désactiver' : 'Activer'} ${lvl.label}`}
                                >
                                  {isActive ? <ToggleRight className="w-3.5 h-3.5" /> : <ToggleLeft className="w-3.5 h-3.5" />}
                                  {lvl.label}
                                </button>
                              );
                            })}
                          </div>
                          {/* Group badges */}
                          {groups.length > 0 && (
                            <div className="flex flex-wrap gap-1.5 mt-2">
                              <span className="text-xs text-gray-500 font-medium self-center mr-1">
                                <FolderOpen className="w-3.5 h-3.5 inline -mt-0.5 mr-0.5" />
                                Groupes :
                              </span>
                              {groups.map(g => {
                                const isMember = (student.groups || []).includes(g.id);
                                return (
                                  <button
                                    key={g.id}
                                    onClick={() => toggleStudentGroup(student, g.id)}
                                    className={`px-2 py-0.5 rounded-full text-xs font-medium transition-all border ${
                                      isMember
                                        ? 'bg-gray-800 text-white border-gray-800'
                                        : 'bg-white text-gray-400 border-gray-300 hover:border-gray-500'
                                    }`}
                                    title={isMember ? `Retirer de ${g.name}` : `Ajouter à ${g.name}`}
                                  >
                                    {isMember && <Check className="w-3 h-3 inline -mt-0.5 mr-0.5" />}
                                    {g.name}
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* ---- STUDENT SESSIONS VIEW ---- */}
        {activeTab === 'students' && viewingSessions && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setViewingSessions(null)}
                  className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm font-medium"
                >
                  Retour
                </button>
                <h2 className="text-2xl font-bold" style={{ color: '#002768' }}>
                  Sessions de {viewingSessions.first_name} {viewingSessions.last_name}
                </h2>
                <Badge variant="outline">#{viewingSessions.student_number}</Badge>
              </div>
              {studentSessions.length > 0 && (
                <div className="flex items-center gap-2">
                  <button
                    onClick={toggleSelectAllSessions}
                    className="text-sm px-3 py-1.5 rounded-lg font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
                    data-testid="admin-select-all-sessions"
                  >
                    {selectedSessionIds.size === studentSessions.length ? 'Tout désélectionner' : 'Tout sélectionner'}
                  </button>
                  {selectedSessionIds.size > 0 && (
                    <button
                      onClick={deleteSelectedSessions}
                      className="text-sm px-3 py-1.5 rounded-lg font-medium bg-rose-500 text-white hover:bg-rose-600 transition-colors flex items-center gap-1"
                      data-testid="admin-delete-selected-sessions"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                      Supprimer ({selectedSessionIds.size})
                    </button>
                  )}
                </div>
              )}
            </div>

            {studentSessions.length === 0 ? (
              <Card className="glass border-0">
                <CardContent className="py-12 text-center text-gray-500">
                  Aucune session pour cet étudiant
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-2">
                {studentSessions.map(session => (
                  <div
                    key={session.id}
                    className={`flex items-center gap-3 p-4 rounded-lg transition-colors ${
                      selectedSessionIds.has(session.id) ? 'bg-rose-50 border-2 border-rose-300' : 'bg-white border-2 border-gray-100 hover:bg-gray-50'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedSessionIds.has(session.id)}
                      onChange={() => toggleSessionSelect(session.id)}
                      className="w-4 h-4 rounded border-gray-300 text-sky-500 focus:ring-sky-500 cursor-pointer flex-shrink-0"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium" style={{ color: '#002768' }}>{session.scenario_title || 'Session'}</span>
                        <Badge variant="outline" className="text-white text-xs"
                          style={{ backgroundColor:
                            session.session_type === 'rao' ? '#8B5CF6' :
                            session.session_type === 'health' ? '#DC2626' :
                            session.scenario_category === 'police' ? '#2E6CF5' :
                            session.scenario_category === 'ambulance' ? '#24B219' : '#F4621C' }}>
                          {session.session_type === 'rao' ? 'RAO' :
                           session.session_type === 'health' ? `Santé${session.mode ? ` · ${session.mode === 'guided' ? 'Guidé' : 'Simulation'}` : ''}` :
                           (session.scenario_category || 'Simulateur')}
                        </Badge>
                        <Badge variant="outline" className={session.status === 'completed' ? 'text-white' : 'bg-amber-50 text-amber-700'}
                               style={session.status === 'completed' ? { backgroundColor: '#24B219' } : {}}>
                          {session.status === 'completed' ? 'Complétée' : 'En cours'}
                        </Badge>
                        {session.session_type === 'health' && session.final_priority && (
                          <Badge variant="outline" className="text-white text-xs"
                            style={{ backgroundColor: session.final_priority === 1 ? '#DC2626' : session.final_priority === 2 ? '#F59E0B' : '#22C55E' }}>
                            Priorité {session.final_priority}
                          </Badge>
                        )}
                        {session.dialogue && (
                          <Badge variant="outline" className="bg-gray-50 text-gray-600">
                            {session.dialogue.length} lignes
                          </Badge>
                        )}
                        {session.transcript && (
                          <Badge variant="outline" className="bg-gray-50 text-gray-600">
                            {session.transcript.length} msg
                          </Badge>
                        )}
                      </div>
                      <div className="text-sm text-gray-500 mt-1">
                        {new Date(session.start_time || session.started_at || session.completed_at).toLocaleDateString('fr-CA', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        {session.duration_seconds ? ` — Durée: ${Math.floor(session.duration_seconds / 60)}:${(session.duration_seconds % 60).toString().padStart(2, '0')}` : ''}
                        {session.duration && !session.duration_seconds ? ` — Durée: ${Math.floor(session.duration / 60)}:${(session.duration % 60).toString().padStart(2, '0')}` : ''}
                      </div>
                      {/* Health-session: show Phase 1 (RAO) answers inline if any were noted */}
                      {session.session_type === 'health' && session.phase1_fields && session.answers && (() => {
                        const entries = session.phase1_fields
                          .map(f => ({ label: f.label, value: (session.answers[f.id] || '').toString().trim() }))
                          .filter(e => e.value);
                        if (entries.length === 0) return null;
                        return (
                          <div className="mt-2 p-2.5 rounded-lg bg-blue-50 border border-blue-200" data-testid={`health-rao-info-${session.id}`}>
                            <div className="text-[11px] uppercase tracking-wide text-blue-700/80 font-bold mb-1">Infos RAO notées par l'étudiant</div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-0.5">
                              {entries.map(e => (
                                <div key={e.label} className="text-xs">
                                  <span className="text-blue-700/70">{e.label}: </span>
                                  <span className="text-blue-900 font-medium">{e.value}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })()}
                      {/* Health-session: show red flags inline if any */}
                      {session.session_type === 'health' && Array.isArray(session.red_flags_detected) && session.red_flags_detected.length > 0 && (
                        <div className="mt-2 p-2 rounded-lg bg-red-50 border border-red-200 text-xs text-red-800">
                          <strong>{session.red_flags_detected.length} drapeau{session.red_flags_detected.length > 1 ? 'x' : ''} rouge{session.red_flags_detected.length > 1 ? 's' : ''}:</strong>{' '}
                          {session.red_flags_detected.slice(0, 4).join(' • ')}{session.red_flags_detected.length > 4 ? ` • +${session.red_flags_detected.length - 4}` : ''}
                        </div>
                      )}
                      {/* Health-session: Volet P1 pre-arrival performance inline */}
                      {session.session_type === 'health' && session.p1_to_end_seconds != null && (
                        <div className="mt-2 p-2 rounded-lg bg-orange-50 border border-orange-200 text-xs text-orange-800 flex flex-wrap items-center gap-x-4 gap-y-1" data-testid={`p1-stats-${session.id}`}>
                          <strong>🚑 Volet P1 :</strong>
                          <span>Délai P1→fin : <strong className="font-mono">{formatChrono(session.p1_to_end_seconds)}</strong></span>
                          <span>Consignes données : <strong>{Object.keys(session.pre_arrival?.instructions || {}).length}</strong></span>
                          <span>Sécurisation : <strong>{Object.keys(session.pre_arrival?.scene || {}).length}/{SCENE_CHECKS.length}</strong></span>
                          {(session.pre_arrival?.info?.age || '').trim() && <span>Âge : <strong>{session.pre_arrival.info.age}</strong></span>}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {session.session_type === 'health' && (
                        <button
                          onClick={() => printHealthSession(session)}
                          className="p-2 rounded-lg hover:bg-red-100 flex-shrink-0"
                          style={{ color: '#DC2626' }}
                          title="Imprimer le rapport santé"
                          data-testid={`print-health-session-${session.id}`}
                        >
                          <Printer className="w-4 h-4" />
                        </button>
                      )}
                      {session.has_retroaction && (
                        <button
                          onClick={() => navigate(`/retroaction/${session.id}`)}
                          className="p-2 rounded-lg hover:bg-orange-100 flex-shrink-0"
                          style={{ color: '#F4621C' }}
                          title="Voir la rétroaction"
                        >
                          <Target className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={() => deleteOneSession(session.id)}
                        className="p-2 rounded-lg hover:bg-rose-100 text-rose-500 flex-shrink-0"
                        title="Supprimer cette session"
                        data-testid={`delete-session-${session.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ---- GROUPS TAB ---- */}
        {activeTab === 'groups' && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold" style={{ color: '#002768' }}>Gestion des groupes</h2>
              <button
                onClick={() => { resetGroupForm(); setShowGroupForm(true); }}
                className="flex items-center gap-2 px-4 py-2 text-white rounded-lg font-medium hover:opacity-90"
                style={{ backgroundColor: '#002768' }}
                data-testid="add-group-button"
              >
                <Plus className="w-4 h-4" />
                Créer un groupe
              </button>
            </div>

            {/* Group Form */}
            {showGroupForm && (
              <Card className="glass border-0 mb-6">
                <CardHeader>
                  <CardTitle>{editingGroup ? 'Modifier le groupe' : 'Nouveau groupe'}</CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={saveGroup} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Nom du groupe</label>
                      <input
                        type="text"
                        value={groupForm.name}
                        onChange={(e) => setGroupForm(prev => ({ ...prev, name: e.target.value }))}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:outline-none"
                        style={{ '--tw-ring-color': '#2E6CF5' }}
                        required
                        placeholder="Ex: Lundi AM, Groupe 01..."
                        data-testid="group-name-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Permissions par défaut du groupe</label>
                      <div className="flex flex-wrap gap-2">
                        {PERM_LABELS.map(perm => (
                          <button
                            key={perm.key}
                            type="button"
                            onClick={() => toggleGroupPerm(perm.key)}
                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                              groupForm.permissions[perm.key] ? 'text-white' : 'bg-gray-200 text-gray-500'
                            }`}
                            style={groupForm.permissions[perm.key] ? { backgroundColor: perm.color } : {}}
                          >
                            {groupForm.permissions[perm.key] ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
                            {perm.label}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Niveaux RAO autorisés</label>
                      <div className="flex flex-wrap gap-2">
                        {RAO_LEVEL_LABELS.map(lvl => {
                          const isActive = (groupForm.rao_levels || []).includes(lvl.key);
                          return (
                            <button
                              key={lvl.key}
                              type="button"
                              onClick={() => setGroupForm(prev => ({
                                ...prev,
                                rao_levels: isActive
                                  ? prev.rao_levels.filter(l => l !== lvl.key)
                                  : [...(prev.rao_levels || []), lvl.key]
                              }))}
                              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                                isActive ? 'text-white' : 'bg-gray-200 text-gray-500'
                              }`}
                              style={isActive ? { backgroundColor: lvl.color } : {}}
                              data-testid={`group-rao-level-${lvl.key}`}
                            >
                              {isActive ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
                              {lvl.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Protocoles Santé (MPDS) accessibles */}
                    <div className="border-t pt-4">
                      <div className="flex items-center justify-between mb-2">
                        <label className="block text-sm font-medium text-gray-700">
                          Protocoles Santé (MPDS) accessibles
                        </label>
                        <div className="flex items-center gap-2 text-xs">
                          <button
                            type="button"
                            onClick={() => setGroupForm(prev => ({ ...prev, enabled_health_protocols: null }))}
                            className="px-2 py-1 rounded bg-gray-100 hover:bg-gray-200 text-gray-700"
                            data-testid="health-protocols-all"
                          >
                            Tous (aucune restriction)
                          </button>
                          <button
                            type="button"
                            onClick={() => setGroupForm(prev => ({ ...prev, enabled_health_protocols: healthProtocolsCatalog.map(p => p.id) }))}
                            className="px-2 py-1 rounded bg-gray-100 hover:bg-gray-200 text-gray-700"
                            data-testid="health-protocols-select-all"
                          >
                            Tout cocher
                          </button>
                          <button
                            type="button"
                            onClick={() => setGroupForm(prev => ({ ...prev, enabled_health_protocols: [] }))}
                            className="px-2 py-1 rounded bg-gray-100 hover:bg-gray-200 text-gray-700"
                            data-testid="health-protocols-clear"
                          >
                            Aucun
                          </button>
                        </div>
                      </div>
                      <p className="text-xs text-gray-500 mb-3">
                        {groupForm.enabled_health_protocols == null
                          ? '« Tous » : les étudiants de ce groupe voient l\'ensemble des protocoles MPDS disponibles.'
                          : `« Liste personnalisée » : ${groupForm.enabled_health_protocols.length} protocole(s) sélectionné(s) sur ${healthProtocolsCatalog.length}.`}
                      </p>
                      {groupForm.enabled_health_protocols != null && (
                        <div className="flex flex-wrap gap-2">
                          {healthProtocolsCatalog.map(p => {
                            const isOn = (groupForm.enabled_health_protocols || []).includes(p.id);
                            return (
                              <button
                                key={p.id}
                                type="button"
                                onClick={() => setGroupForm(prev => {
                                  const cur = prev.enabled_health_protocols || [];
                                  return {
                                    ...prev,
                                    enabled_health_protocols: cur.includes(p.id)
                                      ? cur.filter(id => id !== p.id)
                                      : [...cur, p.id]
                                  };
                                })}
                                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                                  isOn ? 'text-white' : 'bg-gray-200 text-gray-500'
                                }`}
                                style={isOn ? { backgroundColor: '#dc2626' } : {}}
                                data-testid={`group-health-protocol-${p.id}`}
                              >
                                {isOn ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
                                {p.title}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    <div className="flex gap-3">
                      <button
                        type="button"
                        onClick={resetGroupForm}
                        className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200"
                      >
                        Annuler
                      </button>
                      <button
                        type="submit"
                        className="px-6 py-2 text-white rounded-lg font-medium hover:opacity-90"
                        style={{ backgroundColor: '#002768' }}
                        data-testid="save-group-button"
                      >
                        {editingGroup ? 'Enregistrer' : 'Créer le groupe'}
                      </button>
                    </div>
                  </form>
                </CardContent>
              </Card>
            )}

            {/* Groups List */}
            {groups.length === 0 ? (
              <Card className="glass border-0">
                <CardContent className="py-12 text-center">
                  <FolderOpen className="w-12 h-12 mx-auto mb-3" style={{ color: '#CAE9FA' }} />
                  <p className="text-gray-500">Aucun groupe créé</p>
                  <p className="text-sm text-gray-400 mt-1">Créez des groupes pour organiser vos étudiants</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {groups.map(group => {
                  const members = students.filter(s => (s.groups || []).includes(group.id));
                  return (
                    <Card key={group.id} className="glass border-0">
                      <CardContent className="pt-6">
                        <div className="flex items-start justify-between mb-4">
                          <div>
                            <h3 className="text-lg font-bold" style={{ color: '#002768' }}>{group.name}</h3>
                            <p className="text-sm text-gray-500">{group.member_count} membre{group.member_count !== 1 ? 's' : ''}</p>
                          </div>
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => applyGroupPermissions(group)}
                              className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded-lg hover:opacity-90 text-white"
                              style={{ backgroundColor: '#24B219' }}
                              title="Appliquer les permissions à tous les membres"
                              data-testid={`apply-perms-${group.id}`}
                            >
                              <Shield className="w-3.5 h-3.5" />
                              Appliquer aux membres
                            </button>
                            <button
                              onClick={() => { setEditingGroup(group); setGroupForm({ name: group.name, permissions: { ...group.permissions }, rao_levels: [...(group.rao_levels || ['debutant', 'intermediaire', 'avance'])], enabled_health_protocols: group.enabled_health_protocols == null ? null : [...group.enabled_health_protocols] }); setShowGroupForm(true); }}
                              className="p-2 rounded-lg hover:bg-amber-100 text-amber-600"
                              title="Modifier"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => deleteGroup(group.id, group.name)}
                              className="p-2 rounded-lg hover:bg-rose-100 text-rose-600"
                              title="Supprimer"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>

                        {/* Group permissions */}
                        <div className="flex flex-wrap gap-2 mb-4">
                          <span className="text-xs text-gray-500 font-medium self-center">Permissions :</span>
                          {PERM_LABELS.map(perm => (
                            <span
                              key={perm.key}
                              className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                                group.permissions[perm.key] ? 'text-white' : 'bg-gray-200 text-gray-400'
                              }`}
                              style={group.permissions[perm.key] ? { backgroundColor: perm.color } : {}}
                            >
                              {perm.label}
                            </span>
                          ))}
                        </div>

                        {/* RAO Levels */}
                        <div className="flex flex-wrap gap-2 mb-4">
                          <span className="text-xs text-gray-500 font-medium self-center">Niveaux RAO :</span>
                          {RAO_LEVEL_LABELS.map(lvl => {
                            const isActive = (group.rao_levels || ['debutant', 'intermediaire', 'avance']).includes(lvl.key);
                            return (
                              <span
                                key={lvl.key}
                                className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                                  isActive ? 'text-white' : 'bg-gray-200 text-gray-400'
                                }`}
                                style={isActive ? { backgroundColor: lvl.color } : {}}
                              >
                                {lvl.label}
                              </span>
                            );
                          })}
                        </div>

                        {/* Health protocols access */}
                        <div className="flex flex-wrap gap-2 mb-4 items-center" data-testid={`group-health-summary-${group.id}`}>
                          <span className="text-xs text-gray-500 font-medium">Protocoles Santé :</span>
                          {group.enabled_health_protocols == null ? (
                            <span
                              className="px-2.5 py-1 rounded-full text-xs font-semibold text-white"
                              style={{ backgroundColor: '#16a34a' }}
                              title="Aucune restriction — accès à tous les protocoles MPDS"
                            >
                              Tous ({healthProtocolsCatalog.length}/{healthProtocolsCatalog.length})
                            </span>
                          ) : group.enabled_health_protocols.length === 0 ? (
                            <span
                              className="px-2.5 py-1 rounded-full text-xs font-semibold text-white"
                              style={{ backgroundColor: '#9ca3af' }}
                              title="Aucun protocole accessible pour ce groupe"
                            >
                              Aucun (0/{healthProtocolsCatalog.length})
                            </span>
                          ) : (
                            <span
                              className="px-2.5 py-1 rounded-full text-xs font-semibold text-white"
                              style={{ backgroundColor: '#dc2626' }}
                              title={group.enabled_health_protocols
                                .map(id => (healthProtocolsCatalog.find(p => p.id === id) || {}).title || id)
                                .join(' • ')}
                            >
                              Personnalisé ({group.enabled_health_protocols.length}/{healthProtocolsCatalog.length})
                            </span>
                          )}
                        </div>
                        {/* Members list */}
                        {members.length > 0 ? (
                          <div>
                            <p className="text-xs text-gray-500 font-medium mb-2">Membres :</p>
                            <div className="flex flex-wrap gap-2">
                              {members.map(m => (
                                <span key={m.id} className="inline-flex items-center gap-1 px-2.5 py-1 bg-gray-100 rounded-full text-xs text-gray-700">
                                  {m.first_name} {m.last_name}
                                  <button
                                    onClick={() => toggleStudentGroup(m, group.id)}
                                    className="text-gray-400 hover:text-rose-500"
                                    title="Retirer du groupe"
                                  >
                                    <X className="w-3 h-3" />
                                  </button>
                                </span>
                              ))}
                            </div>
                          </div>
                        ) : (
                          <p className="text-xs text-gray-400 italic">Aucun membre — Assignez des étudiants depuis l'onglet Étudiants</p>
                        )}

                        {/* Assign students button */}
                        {assigningGroup?.id === group.id ? (
                          <div className="mt-4 pt-4 border-t border-gray-200">
                            <p className="text-sm font-medium text-gray-700 mb-2">Ajouter des étudiants :</p>
                            <div className="max-h-48 overflow-y-auto space-y-1">
                              {students.filter(s => !(s.groups || []).includes(group.id)).map(s => (
                                <button
                                  key={s.id}
                                  onClick={() => { toggleStudentGroup(s, group.id); loadGroups(); }}
                                  className="flex items-center gap-2 w-full text-left px-3 py-2 rounded-lg hover:bg-gray-100 text-sm"
                                >
                                  <Plus className="w-3.5 h-3.5 text-gray-400" />
                                  {s.first_name} {s.last_name} <span className="text-gray-400">#{s.student_number}</span>
                                </button>
                              ))}
                              {students.filter(s => !(s.groups || []).includes(group.id)).length === 0 && (
                                <p className="text-xs text-gray-400 italic py-2">Tous les étudiants sont déjà dans ce groupe</p>
                              )}
                            </div>
                            <button
                              onClick={() => setAssigningGroup(null)}
                              className="mt-2 text-sm text-gray-500 hover:text-gray-700"
                            >
                              Fermer
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setAssigningGroup(group)}
                            className="mt-3 flex items-center gap-1 text-sm font-medium hover:underline"
                            style={{ color: '#2E6CF5' }}
                          >
                            <Plus className="w-3.5 h-3.5" />
                            Ajouter des étudiants
                          </button>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Help Tab */}
        {activeTab === 'help' && (
          <div data-testid="admin-help-tab">
            <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
              <h2 className="text-2xl font-bold" style={{ color: '#002768' }}>Gestion de l'aide</h2>
              <button
                onClick={() => { setEditingArticle(null); setArticleForm({ title: '', content: '', category: 'etudiant', order: helpArticles.length + 1 }); setShowArticleForm(true); }}
                className="flex items-center gap-2 px-4 py-2 text-white rounded-lg font-medium hover:opacity-90"
                style={{ backgroundColor: '#002768' }}
                data-testid="add-help-article-button"
              >
                <Plus className="w-4 h-4" />
                Nouvel article
              </button>
            </div>

            {/* Article Form */}
            {showArticleForm && (
              <Card className="border-2 mb-6" style={{ borderColor: '#2E6CF5' }}>
                <CardContent className="pt-5 space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className="font-bold" style={{ color: '#002768' }}>
                      {editingArticle ? 'Modifier l\'article' : 'Nouvel article'}
                    </h3>
                    <button onClick={() => { setEditingArticle(null); setArticleForm({ title: '', content: '', category: 'etudiant', order: 0 }); setShowArticleForm(false); }} className="text-gray-400 hover:text-gray-600">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  <input
                    type="text"
                    placeholder="Titre de l'article"
                    value={articleForm.title}
                    onChange={(e) => setArticleForm(prev => ({ ...prev, title: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:outline-none text-sm"
                    data-testid="help-article-title"
                  />
                  <textarea
                    placeholder="Contenu de l'article (supporte le formatage **gras**, les listes avec - et les numéros 1. 2. 3.)"
                    value={articleForm.content}
                    onChange={(e) => setArticleForm(prev => ({ ...prev, content: e.target.value }))}
                    rows={8}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:outline-none text-sm font-mono"
                    data-testid="help-article-content"
                  />
                  <div className="flex items-center gap-4">
                    <div>
                      <label className="text-xs font-medium text-gray-500 mb-1 block">Public cible</label>
                      <select
                        value={articleForm.category}
                        onChange={(e) => setArticleForm(prev => ({ ...prev, category: e.target.value }))}
                        className="px-3 py-2 border rounded-lg text-sm"
                        data-testid="help-article-category"
                      >
                        <option value="etudiant">Étudiant</option>
                        <option value="instructeur">Instructeur</option>
                        <option value="general">Général</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-500 mb-1 block">Ordre</label>
                      <input
                        type="number"
                        value={articleForm.order}
                        onChange={(e) => setArticleForm(prev => ({ ...prev, order: parseInt(e.target.value) || 0 }))}
                        className="w-20 px-3 py-2 border rounded-lg text-sm"
                      />
                    </div>
                    <div className="flex-1" />
                    <button
                      onClick={saveArticle}
                      className="flex items-center gap-2 px-5 py-2 text-white rounded-lg font-medium hover:opacity-90"
                      style={{ backgroundColor: '#24B219' }}
                      data-testid="save-help-article"
                    >
                      <Save className="w-4 h-4" />
                      {editingArticle ? 'Enregistrer' : 'Créer'}
                    </button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Articles list */}
            <div className="space-y-2">
              {helpArticles.map(article => {
                const catColors = { etudiant: '#2E6CF5', instructeur: '#F4621C', general: '#002768' };
                const catLabels = { etudiant: 'Étudiant', instructeur: 'Instructeur', general: 'Général' };
                return (
                  <Card key={article.id} className="border hover:border-gray-300 transition-colors">
                    <CardContent className="py-3 px-4 flex items-center gap-3">
                      <span className="text-sm text-gray-400 font-mono w-6 text-right">{article.order}</span>
                      <div className="flex-1 min-w-0">
                        <span className="font-medium text-sm" style={{ color: '#002768' }}>{article.title}</span>
                        <p className="text-xs text-gray-400 truncate">{article.content.substring(0, 80)}...</p>
                      </div>
                      <Badge variant="outline" className="text-xs text-white flex-shrink-0" style={{ backgroundColor: catColors[article.category] || '#6b7280' }}>
                        {catLabels[article.category] || article.category}
                      </Badge>
                      <button
                        onClick={() => startEditArticle(article)}
                        className="p-1.5 rounded hover:bg-blue-50"
                        style={{ color: '#2E6CF5' }}
                        title="Modifier"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => deleteArticle(article.id)}
                        className="p-1.5 rounded hover:bg-rose-50 text-rose-500"
                        title="Supprimer"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </CardContent>
                  </Card>
                );
              })}
              {helpArticles.length === 0 && (
                <div className="text-center py-12 text-gray-400">
                  <HelpCircle className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>Aucun article d'aide. Cliquez "Nouvel article" pour commencer.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Comments Tab */}
        {activeTab === 'comments' && (
          <div data-testid="admin-comments-tab">
            <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
              <h2 className="text-2xl font-bold" style={{ color: '#002768' }}>Commentaires des étudiants</h2>
              <div className="flex items-center gap-2">
                {['all', 'nouveau', 'lu', 'traite'].map(f => {
                  const labels = { all: 'Tous', nouveau: 'Nouveaux', lu: 'Lus', traite: 'Traités' };
                  const colors = { all: '#6b7280', nouveau: '#EE2130', lu: '#F4621C', traite: '#24B219' };
                  const count = f === 'all' ? comments.length : comments.filter(c => c.status === f).length;
                  return (
                    <button
                      key={f}
                      onClick={() => setCommentFilter(f)}
                      className="px-3 py-1 rounded-full text-xs font-medium border-2 transition-all"
                      style={{
                        borderColor: colors[f],
                        backgroundColor: commentFilter === f ? colors[f] : 'transparent',
                        color: commentFilter === f ? 'white' : colors[f],
                      }}
                      data-testid={`comment-filter-${f}`}
                    >
                      {labels[f]} ({count})
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="space-y-3">
              {comments
                .filter(c => commentFilter === 'all' || c.status === commentFilter)
                .map(comment => {
                  const catIcons = { bug: Bug, suggestion: Lightbulb, question: HelpCircle };
                  const catColors = { bug: '#EE2130', suggestion: '#F4621C', question: '#2E6CF5' };
                  const catLabels = { bug: 'Bug', suggestion: 'Suggestion', question: 'Question' };
                  const statusColors = { nouveau: '#EE2130', lu: '#F4621C', traite: '#24B219' };
                  const statusLabels = { nouveau: 'Nouveau', lu: 'Lu', traite: 'Traité' };
                  const CatIcon = catIcons[comment.category] || Lightbulb;

                  return (
                    <Card key={comment.id} className="border hover:shadow-sm transition-all" data-testid={`comment-${comment.id}`}>
                      <CardContent className="py-4 px-5">
                        <div className="flex items-start gap-3">
                          <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: catColors[comment.category] + '15' }}>
                            <CatIcon className="w-4 h-4" style={{ color: catColors[comment.category] }} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap mb-1">
                              <span className="font-semibold text-sm" style={{ color: '#002768' }}>{comment.student_name}</span>
                              {comment.student_number && (
                                <span className="text-xs text-gray-400">#{comment.student_number}</span>
                              )}
                              <Badge variant="outline" className="text-xs text-white" style={{ backgroundColor: catColors[comment.category] }}>
                                {catLabels[comment.category]}
                              </Badge>
                              <Badge variant="outline" className="text-xs text-white" style={{ backgroundColor: statusColors[comment.status] }}>
                                {statusLabels[comment.status]}
                              </Badge>
                              {comment.page && (
                                <span className="text-xs text-gray-400">Page: {comment.page}</span>
                              )}
                            </div>
                            <p className="text-sm text-gray-700 whitespace-pre-wrap">{comment.message}</p>
                            <p className="text-xs text-gray-400 mt-2">
                              {new Date(comment.created_at).toLocaleDateString('fr-CA', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                            </p>
                          </div>
                          <div className="flex items-center gap-1 flex-shrink-0">
                            {comment.status === 'nouveau' && (
                              <button onClick={() => updateCommentStatus(comment.id, 'lu')}
                                className="px-2 py-1 rounded text-xs font-medium hover:bg-orange-50" style={{ color: '#F4621C' }}
                                title="Marquer comme lu">Lu</button>
                            )}
                            {comment.status !== 'traite' && (
                              <button onClick={() => updateCommentStatus(comment.id, 'traite')}
                                className="px-2 py-1 rounded text-xs font-medium hover:bg-green-50" style={{ color: '#24B219' }}
                                title="Marquer comme traité">Traité</button>
                            )}
                            <button onClick={() => deleteComment(comment.id)}
                              className="p-1.5 rounded hover:bg-rose-50 text-rose-500" title="Supprimer">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              {comments.filter(c => commentFilter === 'all' || c.status === commentFilter).length === 0 && (
                <div className="text-center py-12 text-gray-400">
                  <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>Aucun commentaire {commentFilter !== 'all' ? `avec le statut "${commentFilter}"` : ''}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ---- USAGE TAB ---- */}
        {activeTab === 'corrections' && (
          <CorrectionsTab />
        )}

        {activeTab === 'evaluation' && (
          <EvaluationGridTab onRequestsChange={loadPendingEvalCount} />
        )}

        {activeTab === 'usage' && (
          <UsageTab />
        )}

        {/* ---- ADDRESSES TAB ---- */}
        {activeTab === 'addresses' && (
          <AddressesTab />
        )}

        {/* ---- CONTROL TAB ---- */}
        {activeTab === 'control' && (
          <AppControlTab />
        )}

      </div>
    </div>
  );
};

export default RAOAdmin;
